/**
 * Reconciliation detail modal shell: composes body and footer from invoiceReconciliation components.
 */
import React, { useState } from 'react';
import { Box, Button, Flex, HStack, Icon, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay, ModalCloseButton, Text } from '@chakra-ui/react';
import { TbFlag } from 'react-icons/tb';
import { useToast } from '@chakra-ui/react';
import type { InvoiceDetailResponse, InvoicePart } from '../../types/invoice';
import { getInvoicePartByGlobalPartIndex, globalPartIndexForMatchedNexLines, resolveTabularLocationForGlobalPartIndex } from '../../utils/invoiceGlobalPartIndex';
import { getFlaggedItemMatchOptions, getEffectiveSelected, getEffectiveAccordionSelected } from './nexOptionUtils';
import { useIsOverflowing } from '../../hooks/useIsOverflowing';
import { ReconciliationMatchNoteContent } from './ReconciliationMatchNoteContent';
import { ReconciliationAutoContent } from './ReconciliationAutoContent';
import { VendorMismatchContent } from './VendorMismatchContent';
import { FlaggedItemContent } from './FlaggedItemContent';
import { FlaggedItemFooter } from './FlaggedItemFooter';
import type { InvoicingSettings } from '../../hooks/useGetSettings';
import {
    resolveListPriceAfterManualLkqMatch,
    buildManualMatchLkqListPriceNote,
    lookupCiecaCodeFromNexResponse,
    pricingPartKindFromNexLine,
} from '../../utils/applyManualMatchLkqListPrice';
import type { ReconciliationMatchNote } from '../../types/invoice';

export type ReconciliationDetailModalState = {
    type: 'flag' | 'auto' | 'match_note';
    partIndex?: number;
    issue?: any;
    correction?: any;
    matchNote?: { invoicePartNumber: string; nexPartNumber: string; reason: string; partIndex?: number };
} | null;

export interface InvoiceReconciliationDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    reconciliationDetailModal: ReconciliationDetailModalState;
    invoice: InvoiceDetailResponse | undefined;
    editedFields: Partial<InvoiceDetailResponse>;
    getIssuesForPart: (index: number) => any[];
    getPartNumberChoiceIssue: (partIndex: number) => any;
    getIssueTooltipText: (issue: any, hasLocalMatch?: boolean, asString?: boolean) => any;
    unmatchedPartIndices: Set<number>;
    previouslyMatchedNexLines: Record<number, any>;
    localMatchedNexOverrides: Record<number, any>;
    selectedNexOptionKey: string | null;
    setSelectedNexOptionKey: React.Dispatch<React.SetStateAction<string | null>>;
    selectedNexOptionAccordionKey: string | null;
    setSelectedNexOptionAccordionKey: React.Dispatch<React.SetStateAction<string | null>>;
    setLocalMatchedNexOverrides: React.Dispatch<React.SetStateAction<Record<number, any>>>;
    setLocalReconciliationMatchNotes?: React.Dispatch<
        React.SetStateAction<Record<number, ReconciliationMatchNote>>
    >;
    setUnmatchedPartIndices: React.Dispatch<React.SetStateAction<Set<number>>>;
    setPreviouslyMatchedNexLines: React.Dispatch<React.SetStateAction<Record<number, any>>>;
    updateLineItem: (index: number, field: keyof InvoiceDetailResponse['lineItems'][0], value: any) => void;
    updateField: (field: keyof InvoiceDetailResponse, value: any) => void;
    getLineItemStringValue: (index: number, field: keyof InvoiceDetailResponse['lineItems'][0], defaultValue?: string) => string;
    renderLineItemNumericCell: (index: number, field: 'listPrice' | 'unitBuyPrice' | 'totalBuyPrice', fw?: string) => React.ReactNode;
    updateIgnoredPart?: (index: number, field: keyof InvoiceDetailResponse['lineItems'][0], value: any) => void;
    getIgnoredPartStringValue?: (index: number, field: keyof InvoiceDetailResponse['lineItems'][0], defaultValue?: string) => string;
    renderIgnoredPartNumericCell?: (index: number, field: 'listPrice' | 'unitBuyPrice' | 'totalBuyPrice', fw?: string) => React.ReactNode;
    hasChanges: () => boolean;
    effectiveMatchedNexLines?: Array<{ partIndex: number; nexLine: any }>;
    /** Current line row → last-saved line index, so deleted matches re-enter the NEX pool. */
    lineItemBackendIndexByCurrentIndex?: (number | undefined)[];
    onAcceptVendorFromMismatch?: (vendorName: string) => void;
    /** Re-fetch NEX and re-run reconciliation while the Flagged Item modal is open (editable invoices only). */
    onRefreshNexsyisData?: () => void;
    isRefreshNexsyisPending?: boolean;
    isReadOnly?: boolean;
    nexLineQuantityBasis?: import('../../utils/nexLineQuantity').NexLineQuantityBasis;
    /** Org invoicing settings used to apply used/aftermarket list markup immediately on Match. */
    invoicingSettings?: InvoicingSettings | null;
    /** Shop location state (e.g. CA) for markup row matching. */
    locationState?: string | null;
    /** Vehicle make from the purchase-order VIN, for markup row matching. */
    vehicleMake?: string | null;
}

export default function InvoiceReconciliationDetailModal(props: InvoiceReconciliationDetailModalProps) {
    const {
        isOpen,
        onClose,
        reconciliationDetailModal,
        invoice,
        editedFields,
        getIssuesForPart,
        getPartNumberChoiceIssue,
        getIssueTooltipText,
        unmatchedPartIndices,
        previouslyMatchedNexLines,
        localMatchedNexOverrides,
        selectedNexOptionKey,
        setSelectedNexOptionKey,
        selectedNexOptionAccordionKey,
        setSelectedNexOptionAccordionKey,
        setLocalMatchedNexOverrides,
        setLocalReconciliationMatchNotes,
        setUnmatchedPartIndices,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- part of hook API
        setPreviouslyMatchedNexLines,
        updateLineItem,
        updateField,
        getLineItemStringValue,
        renderLineItemNumericCell,
        updateIgnoredPart,
        getIgnoredPartStringValue,
        renderIgnoredPartNumericCell,
        hasChanges,
        effectiveMatchedNexLines: effectiveMatchedNexLinesProp,
        lineItemBackendIndexByCurrentIndex,
        onAcceptVendorFromMismatch,
        onRefreshNexsyisData,
        isRefreshNexsyisPending = false,
        isReadOnly = false,
        nexLineQuantityBasis = 'qtyRec',
        invoicingSettings = null,
        locationState = null,
        vehicleMake = null,
    } = props;
    const toast = useToast();
    const [selectedVendor, setSelectedVendor] = useState<string | null>(null);
    const isSubmittedOrManual =
        invoice?.status === 'submitted' ||
        invoice?.status === 'manually_submitted';
    const isVendorModalReadOnly = isReadOnly || isSubmittedOrManual;
    const closeReconciliationDetailModal = () => {
        setSelectedVendor(null);
        onClose();
    };

    React.useEffect(() => {
        if (!isOpen) return;
        const issue = reconciliationDetailModal?.type === 'flag' ? reconciliationDetailModal.issue : null;
        if (!issue || issue.issueType !== 'vendor_mismatch') return;
        const initial = issue?.nexVendorName != null ? String(issue.nexVendorName).trim() : '';
        setSelectedVendor(initial.length > 0 ? initial : null);
    }, [isOpen, reconciliationDetailModal]);

    const partIndex = reconciliationDetailModal?.type === 'flag' ? reconciliationDetailModal.partIndex : undefined;
    const currentLineItems = editedFields.lineItems ?? invoice?.lineItems ?? [];
    const currentFreightItems = editedFields.freightItems ?? invoice?.freightItems ?? [];
    const item =
        partIndex != null
            ? getInvoicePartByGlobalPartIndex(partIndex, invoice, editedFields) ??
              (partIndex >= 0 ? currentLineItems[partIndex] : currentFreightItems[-(partIndex + 1)])
            : undefined;
    const issues = partIndex != null ? getIssuesForPart(partIndex) : [];
    const choiceIssue = partIndex != null ? getPartNumberChoiceIssue(partIndex) : null;
    const canonicalNexPartIndex =
        partIndex != null ? globalPartIndexForMatchedNexLines(partIndex, invoice, editedFields) : undefined;
    const isUserUnmatchedLine =
        partIndex != null &&
        (unmatchedPartIndices.has(partIndex) ||
            (canonicalNexPartIndex != null && unmatchedPartIndices.has(canonicalNexPartIndex)));
    const prevMatchedNex =
        partIndex != null
            ? previouslyMatchedNexLines[partIndex] ??
              (canonicalNexPartIndex != null ? previouslyMatchedNexLines[canonicalNexPartIndex] : undefined)
            : undefined;

    const matchResult = partIndex != null && item
        ? getFlaggedItemMatchOptions({
            partIndex,
            item,
            issues,
            choiceIssue: choiceIssue ?? null,
            isUserUnmatchedLine,
            prevMatchedNex,
            previouslyMatchedNexLines,
            localMatchedNexOverrides,
            unmatchedPartIndices,
            invoice,
            editedFields,
            effectiveMatchedNexLines: effectiveMatchedNexLinesProp,
            lineItemBackendIndexByCurrentIndex,
            nexLineQuantityBasis,
        })
        : null;

    const options = matchResult?.options ?? [];
    const nexAccordionLineCount =
        matchResult?.nexOptionsByVendor?.reduce((n, v) => n + (v.lines?.length ?? 0), 0) ?? 0;
    const hasAccordionNexLines = nexAccordionLineCount > 0;
    const issueWithOptions = choiceIssue ?? issues.find((i: any) =>
        (i.issueType === 'missing_in_nex' || i.issueType === 'different_part_names' || i.issueType === 'different_positions') &&
        (i.issueDetails?.suggestedPartNumber || (i.issueDetails?.suggestedNexLinesFromVendor?.length) || i.issueDetails?.suggestedLine || i.issueDetails?.nexOptionsByVendor?.length)
    );
    const issueWithNexOptions = issues.find((i: any) => i.issueType === 'missing_in_nex' || i.issueType === 'different_part_names' || i.issueType === 'different_positions');
    const hasInlineActions = !!(issueWithOptions || (issueWithNexOptions && (issueWithNexOptions.issueDetails?.nexOptionsByVendor?.length ?? 0) > 0));
    const showUnifiedModal = !!matchResult?.showUnifiedModal;
    const unifiedSelected = showUnifiedModal ? getEffectiveSelected(options, selectedNexOptionKey) : null;
    const accordionSelected =
        !showUnifiedModal && hasAccordionNexLines
            ? getEffectiveAccordionSelected(matchResult?.nexOptionsByVendor, selectedNexOptionAccordionKey)
            : null;
    const effectiveSelected = unifiedSelected ?? accordionSelected;
    // Keep Match visible (disabled) when rows exist but CIECA/search hide all of them.
    const showMatchButton =
        partIndex != null &&
        (isUserUnmatchedLine || hasInlineActions || hasAccordionNexLines) &&
        (showUnifiedModal || hasAccordionNexLines);

    const handleMatchClick = () => {
        if (partIndex == null || !effectiveSelected) return;
        const ext = effectiveSelected as unknown as Record<string, unknown>;
        const ciecaFromOption =
            typeof effectiveSelected.ciecaCode === 'string' && effectiveSelected.ciecaCode.trim()
                ? effectiveSelected.ciecaCode.trim()
                : typeof ext.ciecaCode === 'string' && String(ext.ciecaCode).trim()
                  ? String(ext.ciecaCode).trim()
                  : undefined;
        const rawQtyOnOrder = effectiveSelected.qtyOnOrder;
        const rawQtyRec = effectiveSelected.qtyRec;
        const nexLineForMatch = {
            partDesc: effectiveSelected.title ?? ext.partDesc,
            partNumber: effectiveSelected.partNumber,
            ...(rawQtyOnOrder != null && Number.isFinite(rawQtyOnOrder) ? { qtyOnOrder: rawQtyOnOrder } : {}),
            qty: effectiveSelected.qty,
            ...(rawQtyRec != null && Number.isFinite(rawQtyRec) ? { qtyRec: rawQtyRec } : {}),
            listPrice: effectiveSelected.listPrice,
            unitCost: effectiveSelected.unitBuyPrice,
            extendedCost: effectiveSelected.totalBuyPrice,
            poHeaderID: ext.poHeaderID ?? ext.po_header_id,
            lineNumber: ext.lineNumber ?? ext.line_number,
            unqSeq: ext.unqSeq ?? ext.unq_seq,
            ...(ciecaFromOption ? { ciecaCode: ciecaFromOption } : {}),
            _poVendorName: ext._poVendorName,
            ...(ext._nexMatchSource === 'returns' || ext._nexMatchSource === 'orders'
                ? { _nexMatchSource: ext._nexMatchSource }
                : {}),
        };
        const overrideIndex = globalPartIndexForMatchedNexLines(partIndex, invoice, editedFields);
        const ciecaCode =
            ciecaFromOption ??
            lookupCiecaCodeFromNexResponse(invoice?.nexResponse ?? editedFields.nexResponse, {
                poHeaderID:
                    typeof nexLineForMatch.poHeaderID === 'number'
                        ? nexLineForMatch.poHeaderID
                        : Number(nexLineForMatch.poHeaderID),
                lineNumber:
                    typeof nexLineForMatch.lineNumber === 'number'
                        ? nexLineForMatch.lineNumber
                        : Number(nexLineForMatch.lineNumber),
                unqSeq:
                    nexLineForMatch.unqSeq == null
                        ? null
                        : (nexLineForMatch.unqSeq as number | null),
                partNumber: nexLineForMatch.partNumber,
            });
        if (ciecaCode && !(nexLineForMatch as { ciecaCode?: string }).ciecaCode) {
            (nexLineForMatch as { ciecaCode?: string }).ciecaCode = ciecaCode;
        }

        setLocalMatchedNexOverrides((prev) => ({ ...prev, [overrideIndex]: nexLineForMatch }));
        setUnmatchedPartIndices((prev) => {
            const s = new Set(prev);
            s.delete(overrideIndex);
            s.delete(partIndex);
            return s;
        });

        const loc = resolveTabularLocationForGlobalPartIndex(overrideIndex, invoice, editedFields);
        if (loc?.kind === 'line') {
            const linePart =
                (editedFields.lineItems ?? invoice?.lineItems ?? [])[loc.localIndex] ?? item;
            if (linePart) {
                const partTypeFromOption =
                    typeof (effectiveSelected as { partType?: string }).partType === 'string'
                        ? (effectiveSelected as { partType?: string }).partType
                        : undefined;
                const resolved = resolveListPriceAfterManualLkqMatch(linePart, ciecaCode, {
                    invoiceType: editedFields.invoiceType ?? invoice?.invoiceType,
                    lkqListPriceMarkup: editedFields.lkqListPriceMarkup ?? invoice?.lkqListPriceMarkup,
                    lkqListPriceDiscount:
                        editedFields.lkqListPriceDiscount ?? invoice?.lkqListPriceDiscount,
                    invoicingSettings,
                    insuranceCompanyId: invoice?.internalInsuranceCompanyId ?? null,
                    supplierId: invoice?.internalSupplierId ?? null,
                    locationState,
                    partKind: pricingPartKindFromNexLine({
                        partType: partTypeFromOption,
                        ciecaCode,
                    }),
                    vehicleMake: vehicleMake ?? invoice?.vehicleMake ?? null,
                });
                if (resolved?.mode === 'discount' || resolved?.mode === 'billAtCost') {
                    if (resolved.listPrice !== Number(linePart.listPrice ?? 0)) {
                        updateLineItem(loc.localIndex, 'listPrice', resolved.listPrice);
                    }
                    if (Math.abs(resolved.unitBuyPrice - Number(linePart.unitBuyPrice ?? 0)) >= 0.005) {
                        updateLineItem(loc.localIndex, 'unitBuyPrice', resolved.unitBuyPrice);
                    }
                } else if (resolved && resolved.listPrice !== Number(linePart.listPrice ?? 0)) {
                    updateLineItem(loc.localIndex, 'listPrice', resolved.listPrice);
                }
                const note = resolved
                    ? buildManualMatchLkqListPriceNote({
                          partIndex: overrideIndex,
                          invoicePartNumber: linePart.partNumber,
                          nexPartNumber: String(nexLineForMatch.partNumber ?? linePart.partNumber),
                          result: resolved,
                      })
                    : null;
                if (setLocalReconciliationMatchNotes) {
                    setLocalReconciliationMatchNotes((prev) => {
                        const next = { ...prev };
                        if (note) next[overrideIndex] = note;
                        else delete next[overrideIndex];
                        return next;
                    });
                }
            }
        } else if (setLocalReconciliationMatchNotes) {
            setLocalReconciliationMatchNotes((prev) => {
                if (!(overrideIndex in prev)) return prev;
                const next = { ...prev };
                delete next[overrideIndex];
                return next;
            });
        }

        closeReconciliationDetailModal();
        toast({
            title: 'Line matched',
            description: 'The line item has been matched to the selected NEX line. Click Save to persist changes.',
            status: 'info',
            duration: 3000,
            isClosable: true,
        });
    };

    const modalTitle = reconciliationDetailModal?.type === 'flag'
        ? (reconciliationDetailModal.partIndex == null && reconciliationDetailModal.issue?.issueType === 'vendor_mismatch'
            ? 'Vendor Selection'
            : 'Flagged Item')
        : reconciliationDetailModal?.type === 'match_note'
            ? 'Match note'
            : 'Auto Reconciliation';
    const isVendorMismatchModal =
        reconciliationDetailModal?.type === 'flag' &&
        reconciliationDetailModal.partIndex == null &&
        reconciliationDetailModal.issue?.issueType === 'vendor_mismatch';
    const [bodyRef, bodyOverflows] = useIsOverflowing(isOpen && isVendorMismatchModal);

    const roForRefresh = String(editedFields.roNumber ?? invoice?.roNumber ?? '').trim();
    const canRefreshNexsyis = !isReadOnly && roForRefresh.length > 0 && typeof onRefreshNexsyisData === 'function';
    const showRefreshNexsyisInFooter = partIndex != null && canRefreshNexsyis;
    const showRefreshNexsyisInVendorModal = isVendorMismatchModal && canRefreshNexsyis;

    const isIgnoredTabEdit = partIndex != null && (() => {
        const loc = resolveTabularLocationForGlobalPartIndex(partIndex, invoice, editedFields);
        return loc?.kind === 'ignored';
    })();
    const flagBannerText = reconciliationDetailModal?.type === 'flag' && partIndex != null && item
        ? (isUserUnmatchedLine && issues.length === 0
            ? isIgnoredTabEdit
                ? 'This return slip line is unmatched from NEX /returns. Choose a NEX line below or save after editing.'
                : 'Line item will be added to NEX without a matching NEX line'
            : issues.map((i: any) => getIssueTooltipText(i, false, true)).join('; '))
        : null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} size={isVendorMismatchModal ? 'md' : '6xl'} isCentered scrollBehavior={isVendorMismatchModal ? 'inside' : undefined}>
            <ModalOverlay />
            <ModalContent maxW={isVendorMismatchModal ? '520px' : '90vw'} h={isVendorMismatchModal ? undefined : '85vh'} borderRadius="xl" overflow="hidden">
                <ModalHeader
                    borderBottomWidth={bodyOverflows ? '1px' : '0'}
                    borderBottomStyle="solid"
                    borderColor="gray.300"
                >{modalTitle}</ModalHeader>
                <ModalCloseButton />
                {flagBannerText && (
                    <Box px={6} pt={4} pb={2} flexShrink={0}>
                        <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="md" p={3}>
                            <HStack spacing={2} align="flex-start">
                                <Icon as={TbFlag as any} color="orange.500" boxSize={4} flexShrink={0} mt="1px" />
                                <Text fontSize="sm" color="orange.700" fontWeight="medium">{flagBannerText}</Text>
                            </HStack>
                        </Box>
                    </Box>
                )}
                <ModalBody ref={bodyRef} minH={0} pb={6} overflow="hidden" flex="1" display="flex" flexDirection="column">
                    {reconciliationDetailModal?.type === 'match_note' && reconciliationDetailModal.matchNote && (() => {
                        const mn = reconciliationDetailModal.matchNote;
                        const partIdx = mn.partIndex;
                        const lineItems = editedFields.lineItems ?? invoice?.lineItems ?? [];
                        const invoicePart = partIdx != null ? lineItems[partIdx] : null;
                        const reconciliationIssues = invoice?.reconciliationIssues as {
                            probableMatches?: Array<{ partIndex: number; invoicePart: InvoicePart; nexLine: unknown }>;
                            matchedNexLines?: Array<{ partIndex: number; nexLine: unknown }>;
                        } | undefined;
                        const probableMatches = reconciliationIssues?.probableMatches;
                        const matchedNexLines = reconciliationIssues?.matchedNexLines;
                        const pm = partIdx != null ? probableMatches?.find((p: { partIndex: number }) => p.partIndex === partIdx) : undefined;
                        const mnLine = partIdx != null ? matchedNexLines?.find((m: { partIndex: number }) => m.partIndex === partIdx) : undefined;
                        // Match note modal should reflect the same matched NEX line used by the
                        // expanded "Matched NEX line" row in line-item tabs.
                        const nexLine = (mnLine?.nexLine ?? pm?.nexLine) as {
                            partDesc?: string; description?: string; partNumber?: string;
                            qtyRec?: number; qtyReturned?: number; qtyOnOrder?: number; qty?: number; quantity?: number;
                            unitCost?: number; unit_price?: number; extendedCost?: number; extended_cost?: number;
                            listPrice?: number; list_price?: number;
                        } | undefined;

                        const lineItemCorrections = invoice?.lineItemCorrectionMetadata ?? [];
                        const expansionCorrection =
                            partIdx != null
                                ? lineItemCorrections.find(
                                      (c): c is (typeof c) & { originalLine: InvoicePart; newIndices: number[] } =>
                                          c.type === 'expanded_to_match_nex' &&
                                          !!c.originalLine &&
                                          Array.isArray(c.newIndices) &&
                                          c.newIndices.length > 1 &&
                                          c.newIndices.includes(partIdx)
                                  )
                                : undefined;

                        const nexByPartIndex: Record<number, unknown> = {};
                        (effectiveMatchedNexLinesProp ?? matchedNexLines ?? []).forEach((m: { partIndex: number; nexLine: unknown }) => {
                            nexByPartIndex[m.partIndex] = m.nexLine;
                        });
                        probableMatches?.forEach((p: { partIndex: number; nexLine: unknown }) => {
                            if (nexByPartIndex[p.partIndex] == null) nexByPartIndex[p.partIndex] = p.nexLine;
                        });
                        Object.entries(localMatchedNexOverrides).forEach(([idxStr, nl]) => {
                            const idx = parseInt(idxStr, 10);
                            if (!Number.isNaN(idx)) nexByPartIndex[idx] = nl;
                        });

                        let matchNoteExpansion: { originalInvoiceLine: InvoicePart; nexLinesInOrder: NonNullable<typeof nexLine>[] } | null = null;
                        if (expansionCorrection?.newIndices && expansionCorrection.originalLine) {
                            const ordered = expansionCorrection.newIndices
                                .map((idx) => nexByPartIndex[idx])
                                .filter((x) => x != null) as NonNullable<typeof nexLine>[];
                            if (ordered.length === expansionCorrection.newIndices.length) {
                                matchNoteExpansion = {
                                    originalInvoiceLine: expansionCorrection.originalLine,
                                    nexLinesInOrder: ordered,
                                };
                            }
                        }
                        // When rows are not yet matched to NEX, matchedNexLines is empty — still show the 1→N diagram
                        // using structure-pass suggested NEX lines from amount-mismatch issues (same order as expansion).
                        if (
                            !matchNoteExpansion &&
                            expansionCorrection?.newIndices &&
                            expansionCorrection.originalLine
                        ) {
                            const flagged = (
                                invoice?.reconciliationIssues as
                                    | {
                                          flaggedParts?: Array<{
                                              partIndex: number;
                                              issueType: string;
                                              issueDetails?: { suggestedLineItems?: InvoicePart[] };
                                          }>;
                                      }
                                    | undefined
                            )?.flaggedParts;
                            const newIdxSet = new Set(expansionCorrection.newIndices);
                            const amo = flagged?.find(
                                (f) =>
                                    (f.issueType === 'amount_mismatch_multiple_nex_lines' ||
                                        f.issueType === 'amount_mismatch_nex_structure') &&
                                    newIdxSet.has(f.partIndex) &&
                                    Array.isArray(f.issueDetails?.suggestedLineItems) &&
                                    (f.issueDetails!.suggestedLineItems!.length ?? 0) >= expansionCorrection.newIndices.length
                            );
                            const suggested = amo?.issueDetails?.suggestedLineItems;
                            if (suggested && suggested.length >= expansionCorrection.newIndices.length) {
                                const slice = suggested.slice(0, expansionCorrection.newIndices.length);
                                matchNoteExpansion = {
                                    originalInvoiceLine: expansionCorrection.originalLine,
                                    nexLinesInOrder: slice.map((s) => ({
                                        partDesc: s.title,
                                        description: s.title,
                                        partNumber: s.partNumber,
                                        qty: s.qty,
                                        listPrice: s.listPrice,
                                        unitCost: s.unitBuyPrice,
                                        extendedCost: s.totalBuyPrice,
                                    })),
                                };
                            }
                        }

                        return (
                            <ReconciliationMatchNoteContent
                                matchNote={mn}
                                invoicePart={invoicePart ?? null}
                                invoiceType={invoice?.invoiceType}
                                nexLineQuantityBasis={nexLineQuantityBasis}
                                nexLine={nexLine ?? null}
                                matchNoteExpansion={matchNoteExpansion}
                            />
                        );
                    })()}

                    {reconciliationDetailModal?.type === 'flag' && reconciliationDetailModal.partIndex != null && (() => {
                        const idx = reconciliationDetailModal.partIndex!;
                        if (!item) return <Text color="gray.500">Line item not found.</Text>;
                        const loc = resolveTabularLocationForGlobalPartIndex(idx, invoice, editedFields);
                        const tabularEdit = loc?.kind === 'ignored' ? { kind: 'ignored' as const, rowIndex: loc.localIndex } : undefined;
                        return (
                            <FlaggedItemContent
                                idx={idx}
                                item={item}
                                issues={issues}
                                choiceIssue={choiceIssue}
                                isUserUnmatchedLine={isUserUnmatchedLine}
                                getIssueTooltipText={getIssueTooltipText}
                                matchResult={matchResult}
                                selectedNexOptionKey={selectedNexOptionKey}
                                setSelectedNexOptionKey={setSelectedNexOptionKey}
                                selectedNexOptionAccordionKey={selectedNexOptionAccordionKey}
                                setSelectedNexOptionAccordionKey={setSelectedNexOptionAccordionKey}
                                invoice={invoice}
                                editedFields={editedFields}
                                updateLineItem={updateLineItem}
                                getLineItemStringValue={getLineItemStringValue}
                                renderLineItemNumericCell={renderLineItemNumericCell}
                                tabularEdit={tabularEdit}
                                updateIgnoredPart={updateIgnoredPart}
                                getIgnoredPartStringValue={getIgnoredPartStringValue}
                                renderIgnoredPartNumericCell={renderIgnoredPartNumericCell}
                                nexLineQuantityBasis={nexLineQuantityBasis}
                            />
                        );
                    })()}

                    {reconciliationDetailModal?.type === 'flag' && reconciliationDetailModal.partIndex == null && reconciliationDetailModal.issue && (
                        <VendorMismatchContent
                            issue={reconciliationDetailModal.issue}
                            getIssueTooltipText={getIssueTooltipText}
                            selectedVendor={selectedVendor}
                            onSelectVendor={setSelectedVendor}
                            readOnly={isVendorModalReadOnly}
                            showSubmittedCopy={isSubmittedOrManual}
                        />
                    )}

                    {reconciliationDetailModal?.type === 'auto' && reconciliationDetailModal.correction && (
                        <ReconciliationAutoContent
                            correction={reconciliationDetailModal.correction}
                            invoiceType={invoice?.invoiceType}
                            nexLineQuantityBasis={nexLineQuantityBasis}
                        />
                    )}
                </ModalBody>

                {reconciliationDetailModal?.type === 'flag' && partIndex != null && (
                    <ModalFooter>
                        <FlaggedItemFooter
                            onClose={closeReconciliationDetailModal}
                            onMatch={handleMatchClick}
                            onDoneWithoutMatch={() => {
                                closeReconciliationDetailModal();
                                toast({
                                    title: 'Changes saved locally',
                                    description: 'Click Save to persist changes.',
                                    status: 'info',
                                    duration: 3000,
                                    isClosable: true,
                                });
                            }}
                            showMatchButton={!!showMatchButton}
                            effectiveSelected={effectiveSelected}
                            hasChanges={hasChanges}
                            showDoneButton={!showMatchButton}
                            onRefreshNexsyisData={showRefreshNexsyisInFooter ? onRefreshNexsyisData : undefined}
                            isRefreshNexsyisPending={isRefreshNexsyisPending}
                        />
                    </ModalFooter>
                )}

                {isVendorMismatchModal && (
                    <ModalFooter>
                        <Flex w="100%" justify="space-between" align="center" flexWrap="wrap" gap={3}>
                            {showRefreshNexsyisInVendorModal && !isVendorModalReadOnly && (
                                <Button
                                    size="action"
                                    variant="outline"
                                    colorScheme="blue"
                                    isLoading={isRefreshNexsyisPending}
                                    isDisabled={isRefreshNexsyisPending}
                                    onClick={() => onRefreshNexsyisData?.()}
                                >
                                    Refresh Nexsyis data
                                </Button>
                            )}
                            {isVendorModalReadOnly ? (
                                <Button size="action" ml="auto" onClick={closeReconciliationDetailModal}>
                                    Close
                                </Button>
                            ) : (
                                <Button
                                    size="action"
                                    bg="#0071E3"
                                    color="white"
                                    _hover={{ bg: '#3544a0' }}
                                    isDisabled={!selectedVendor}
                                    ml="auto"
                                    onClick={() => {
                                        if (!selectedVendor) return;
                                        if (onAcceptVendorFromMismatch) {
                                            // Parent may show a confirmation modal before saving/reconciling.
                                            onAcceptVendorFromMismatch(selectedVendor);
                                            closeReconciliationDetailModal();
                                            return;
                                        }
                                        updateField('supplierText', selectedVendor);
                                        closeReconciliationDetailModal();
                                        toast({
                                            title: 'Vendor name updated',
                                            description: 'Reconciliation has been rerun with the selected vendor.',
                                            status: 'info',
                                            duration: 3000,
                                            isClosable: true,
                                        });
                                    }}
                                >
                                    Confirm
                                </Button>
                            )}
                        </Flex>
                    </ModalFooter>
                )}
            </ModalContent>
        </Modal>
    );
}
